import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSilosQuery } from './Silos.generated';
import SingleDeliveryItem from './SingleDeliveryItem';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  DeliveryComment,
  SingleDeliverySiloForm,
  reformatOrderAddress,
} from '@web/utils';
import { userAtom } from '@/store/atoms';
import {
  changeDeliveryAddressAtom,
  changeDeliveryDatesAtom,
  changeLocationIDAtom,
  changeSiloIdAtom,
  moveBetweenDeliveriesAtom,
  removeFromDeliveryAtom,
  updateDeliyveryCommentAtom,
} from '@/store/store';
import { OrderDelivery, OrderDeliveryAddress, OrderItem } from '@/store/types';
import { Address, DeliveryType, Location } from '@/types';

interface Props {
  delivery: OrderDelivery;
}

const SingleDeliveryTypeTable = (props: Props) => {
  const { delivery } = props;
  const { t } = useTranslation();
  const { retailerId = '1' } = useParams();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    delivery.deliveryAddress.id,
  );
  const [selectedLocationid, setSelectedLocationId] = useState<string | null>(
    delivery.locationID || '1',
  );
  const [selectedLocations, setSelectedLocations] = useState<Location[][]>([]);

  const user = useAtomValue(userAtom);
  const removeMultipleItems = useSetAtom(removeFromDeliveryAtom);
  const changeDeliveryAddress = useSetAtom(changeDeliveryAddressAtom);
  const changeLocationID = useSetAtom(changeLocationIDAtom);
  const moveBetweenDeliveries = useSetAtom(moveBetweenDeliveriesAtom);
  const changeSiloId = useSetAtom(changeSiloIdAtom);
  const changeDates = useSetAtom(changeDeliveryDatesAtom);
  const updateDeliveryComment = useSetAtom(updateDeliyveryCommentAtom);

  const addressLine = (address: Omit<Address, 'owner' | 'ownerId'>) => {
    return `${address.addressLine1}, ${address.addressLine2 || ''} ${
      address.addressLocality
    }, ${address.addressPostcode}`;
  };

  const handleCheckboxChange = (item: OrderItem) => {
    const itemId = item.catalogueID;
    setSelectedItems([itemId.toString()]);
    setSelectedLocations([item.locations]);
  };

  const handleDeleteItems = () => {
    removeMultipleItems({
      retailerId,
      deliveryId: delivery.id,
      catalogueIds: selectedItems,
    });

    setSelectedItems([]);
  };

  const handleUpdateDeliveryLocationID = (value: string) => {
    if (!value) return;
    setSelectedLocationId(value);

    changeLocationID({
      retailerId,
      deliveryId: delivery.id,
      locationID: value,
    });
  };

  const handleUpdateShipmentAddress = (value: string) => {
    if (!user) return;
    setSelectedAddressId(value);
    // get users address with id of value
    const address = user.addresses.find((address) => address.id == value);

    const newDeliveryAddress: OrderDeliveryAddress =
      reformatOrderAddress(address);

    changeDeliveryAddress({
      retailerId,
      deliveryId: delivery.id,
      newAddress: newDeliveryAddress,
    });
  };
  const [{ data }] = useSilosQuery();
  const silos = data?.currentUser?.silos || [];

  const handleSwitchShipment = (id: string | null) => {
    if (!user) return;

    moveBetweenDeliveries({
      retailerId,
      sourceDeliveryId: delivery.id,
      targetDeliveryId: id,
      catalogueIds: selectedItems,
      siloId: silos[0]?.id,
    });

    setSelectedItems([]);
  };

  const changeDeliverySilo = (id: string) => {
    if (!id) return;
    changeSiloId({
      retailerId,
      deliveryId: delivery.id,
      siloId: id,
    });
  };

  const changeDeliveryDates = (
    delivery: OrderDelivery,
    key: string,
    value: string | number,
  ) => {
    if (!value) return;
    changeDates({
      retailerId,
      deliveryId: delivery.id,
      [key]: value,
    });
  };

  const handleAddCommentToDelivery = (comment: string) => {
    if (!comment) return;
    updateDeliveryComment({
      retailerId,
      deliveryId: delivery.id,
      comment,
    });
  };

  const title = useMemo(
    () =>
      `${
        delivery.siloID
          ? t('checkout.silo_shipment')
          : delivery.type === DeliveryType.Pickup
          ? t('order.pickup')
          : t('order.delivery')
      } ${delivery.id}`,
    [delivery.id, delivery.siloID, delivery.type, t],
  );

  return (
    <div className="mb-4-5" id={delivery.id.toString()}>
      <p className="text-body1 font-bd mb-1-6">{title}</p>
      <div className="flex justify-between items-end">
        <div>
          {delivery.type === DeliveryType.Delivery && !delivery.siloID && (
            <>
              <p className="mb-0-6 text-2-0 md:text-1-2 leading-1-33 font-li">
                {t('checkout.date_and_address')}
              </p>
              <select
                className="w-full max-w-30-0 rounded-8-0 py-0-8 px-1-4 border-secondary-grey text-body1 leading-1-25 border bg-white"
                value={selectedAddressId || 1}
                onChange={(e) => handleUpdateShipmentAddress(e.target.value)}
                data-testid={`delivery-select-${delivery.id}`}
              >
                {user?.addresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    {addressLine(address)}
                  </option>
                ))}
              </select>
            </>
          )}
          {delivery.type === DeliveryType.Pickup &&
            (delivery.items[0].locations?.length > 1 ? (
              <select
                className="w-full max-w-30-0 rounded-8-0 py-0-8 px-1-4 border-secondary-grey text-body1 leading-1-25 border bg-white"
                data-testid={`delivery-location-${delivery.id}`}
                value={selectedLocationid || 1}
                onChange={(e) => handleUpdateDeliveryLocationID(e.target.value)}
              >
                {delivery.items[0].locations?.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="w-full max-w-30-0 rounded-8-0 py-0-8 px-1-4 border-secondary-grey text-body1 leading-1-25 border bg-white">
                <span>{delivery.items[0].locations[0].name}</span>
              </div>
            ))}
        </div>
        <SingleDeliverySiloForm
          delivery={delivery}
          changeDeliverySilo={changeDeliverySilo}
          handleSiloDateUpdate={changeDeliveryDates}
          t={t}
          silos={silos}
        />
      </div>
      <div className="mt-1-0 border border-secondary-grey rounded-8-0 p-1-0">
        <div className="hidden py-3-0 md:py-1-0 border-b last:border-0 border-secondary-grey md:flex flex-col md:flex-row  items-start md:items-center relative">
          <div className="md:w-1/3 flex items-center mb-2-0 md:mb-0 pr-3-0 md:pr-0">
            <span className="text-1-4 font-rg">
              {t('checkout.product_name')}
            </span>
          </div>
          <div className="md:w-1/3">
            <span className="text-1-4 font-rg">{t('checkout.amount')}</span>
          </div>
          <div className="ml-auto pr-10-0">
            <span className="text-1-4 font-rg">{t('checkout.cost')}</span>
          </div>
        </div>
        {delivery.items.map((item) => (
          <SingleDeliveryItem
            key={item.catalogueID}
            item={item}
            handleMoveItem={handleSwitchShipment}
            selectedLocations={selectedLocations}
            handleCheckboxChange={handleCheckboxChange}
            delivery={delivery}
            handleDeleteItems={handleDeleteItems}
            canMoveToSilo={!!silos.length}
          />
        ))}
      </div>
      <div className="flex justify-between mt-0-5 items-center">
        <div>
          <p className="text-1-4 font-md">{t('inventory.comment')}:</p>
          {delivery.comments && <p className="text-1-2">{delivery.comments}</p>}
        </div>
        <DeliveryComment
          t={t}
          handleAddCommentToDelivery={handleAddCommentToDelivery}
          isEdit={!!delivery.comments}
        />
      </div>
    </div>
  );
};

export default SingleDeliveryTypeTable;
